import { FormError, FormLayout, Label, Select } from '@kcftech/design-system';
import {
    ApplicationRoleCode,
    ILocationResponse,
    KcfHandling,
    NotificationType,
} from '@kcftech/prometheus-client';
import { LocationDropdown } from 'components/dropdown/LocationDropdown';
import { User } from 'components/dropdown/UserDropdown';
import { UserLocationDropdown } from 'components/dropdown/UserLocationDropdown';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { UsersInput } from 'components/users/UsersInput';
import { usePermissions } from 'contexts/PermissionsContext';
import { useUser } from 'contexts/UserContext';
import { HardwareIssuesNotificationSubscriptionFormContent } from 'pages/Settings/NotificationSubscriptions/form/HardwareIssuesNotificationSubscriptionFormContent';
import { HardwareNotificationSubscriptionFormContent } from 'pages/Settings/NotificationSubscriptions/form/HardwareNotificationSubscriptionFormContent';
import { IndicatorAlertsNotificationSubscriptionFormContent } from 'pages/Settings/NotificationSubscriptions/form/IndicatorAlertsNotificationSubscriptionFormContent';
import { MachineHealthNotificationSubscriptionFormContent } from 'pages/Settings/NotificationSubscriptions/form/MachineHealthNotificationSubscriptionFormContent';
import {
    canViewDraftIssues,
    hasDraftNotificationsEnabled,
    hasTextEnabled,
} from 'pages/Settings/NotificationSubscriptions/form/shared';
import {
    CreateNotificationSubscriptionFormData,
    CreateNotificationSubscriptionFormInputProps,
    SupportedNotificationType,
} from 'pages/Settings/NotificationSubscriptions/form/types';
import { formattedNotificationTypes } from 'pages/Settings/NotificationSubscriptions/shared';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Controller, RegisterOptions, useFormState, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { Lookup } from 'utils/types';

const StyledLocationDropdown = styled(LocationDropdown)`
    margin-bottom: 0 !important;
`;

const supportedTypes: SupportedNotificationType[] = [
    NotificationType.Indicator,
    NotificationType.Hardware,
    NotificationType.HardwareHealth,
    NotificationType.MachineHealth,
];

const notificationTypeOptions = supportedTypes.map((type) => ({
    title: formattedNotificationTypes[type],
    value: type,
}));

const typeRules: RegisterOptions<CreateNotificationSubscriptionFormData, 'type'> = {
    required: 'Notification subscription type is required',
};

const notificationSubscriptionFormContentLookup: Lookup<
    SupportedNotificationType,
    FunctionComponent<CreateNotificationSubscriptionFormInputProps>
> = {
    [NotificationType.Indicator]: IndicatorAlertsNotificationSubscriptionFormContent,
    [NotificationType.Hardware]: HardwareNotificationSubscriptionFormContent,
    [NotificationType.HardwareHealth]: HardwareIssuesNotificationSubscriptionFormContent,
    [NotificationType.MachineHealth]: MachineHealthNotificationSubscriptionFormContent,
};

function validateUser(user: User, formValues: CreateNotificationSubscriptionFormData) {
    if (hasTextEnabled(formValues) && !user.PhoneNumber) {
        return "User does not have a valid phone number. Phone number can be added / edited from the user's profile.";
    }

    if (
        hasDraftNotificationsEnabled(formValues) &&
        !canViewDraftIssues(user, formValues.location)
    ) {
        return 'User has insufficient permissions to view draft issues. Please deselect this user, or uncheck the Draft Issues checkboxes.';
    }

    return undefined;
}

export function CreateNotificationSubscriptionFormContent({
    form,
    corporation,
    location: navigationLocation,
}: Pick<
    CreateNotificationSubscriptionFormInputProps,
    'form' | 'corporation' | 'location'
>) {
    const {
        control,
        register,
        setValue,
        trigger,
    } = form;

    const watchedValues = useWatch({ control });
    const { errors, isSubmitted } = useFormState({ control });

    const { user: currentUser } = useUser();
    const {
        canCreateNotificationSubscriptionsForOthers,
        canAddKcfToNotificationSubscriptions,
    } = usePermissions();

    const [userFilterText, setUserFilterText] = useState('');

    const [selectedUsers, setSelectedUsers] = useState<User[]>(() =>
        form.getValues('users'),
    );

    const usersInfo = useMemo(
        () =>
            selectedUsers.map((user) => ({
                ...user,
                error: validateUser(
                    user,
                    watchedValues as CreateNotificationSubscriptionFormData,
                ),
            })),
        [selectedUsers, watchedValues],
    );

    useEffect(() => {
        setValue('users', selectedUsers);

        if (isSubmitted) {
            // Note that we're using trigger here instead of shouldValidate
            // because the whole form is what _actually_ has the user validations,
            // which do not seem to validate properly when just touching "users"
            trigger();
        }
    }, [isSubmitted, selectedUsers, setValue, trigger]);

    const SubscriptionFormContent =
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        notificationSubscriptionFormContentLookup[watchedValues.type!];

    const hasPresetLocation = navigationLocation !== undefined;

    return (
        <FormLayout>
            <Select
                required
                label="Type"
                error={errors.type?.message}
                options={notificationTypeOptions}
                {...register('type', typeRules)}
            />
            <ValidatableLabel label="Location" error={errors.location?.message} required>
                {canCreateNotificationSubscriptionsForOthers ? (
                    <Controller
                        control={control}
                        name="location"
                        render={({ field: { value, onChange } }) => (
                            <StyledLocationDropdown
                                value={value}
                                onChange={onChange}
                                className="full-width mb-15"
                                corporationId={corporation?.Id}
                                disabled={hasPresetLocation}
                            />
                        )}
                    />
                ) : (
                    currentUser && (
                        <Controller
                            control={control}
                            name="location"
                            render={({ field: { value, onChange } }) => (
                                <UserLocationDropdown
                                    selectedUserId={currentUser.Id}
                                    value={value}
                                    onChange={onChange}
                                    className="full-width mb-15"
                                    corporationId={corporation?.Id}
                                    disabled={hasPresetLocation}
                                />
                            )}
                        />
                    )
                )}
            </ValidatableLabel>
            <SubscriptionFormContent
                form={form}
                corporation={corporation}
                location={watchedValues.location as ILocationResponse | undefined}
                hasPresetLocation={hasPresetLocation}
                setSelectedUsers={setSelectedUsers}
            />

            {canCreateNotificationSubscriptionsForOthers && (
                <>
                    <Label label="Users" required>
                        <UsersInput
                            corporationId={
                                corporation?.Id ??
                                watchedValues.location?.Corporation?.CorporationId
                            }
                            locationId={watchedValues.location?.Id}
                            kcfHandling={
                                canAddKcfToNotificationSubscriptions
                                    ? KcfHandling.Include
                                    : KcfHandling.Hide
                            }
                            applicationRoleCodes={
                                watchedValues.hardwareIssues?.allowInternalOnly
                                    ? [
                                          ApplicationRoleCode.Sales,
                                          ApplicationRoleCode.Sentry,
                                          ApplicationRoleCode.GlobalAdmin,
                                      ]
                                    : undefined
                            }
                            whereAdmin
                            filterText={userFilterText}
                            onFilterTextChange={setUserFilterText}
                            value={usersInfo}
                            userResponseToUserInfo={(res) => res}
                            onChange={setSelectedUsers}
                            disabled={!watchedValues.location}
                            title={
                                !watchedValues.location
                                    ? 'Please select a location first'
                                    : undefined
                            }
                        />
                    </Label>
                    <FormError
                        message={
                            errors.users?.find?.((err) => err !== undefined)?.Id?.message
                        }
                    />
                </>
            )}
        </FormLayout>
    );
}
